import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  User,
  CreditCard,
  LogOut,
  Shield,
  Home,
  Play,
  Settings,
  ChevronRight,
  HelpCircle,
  Megaphone,
  Trophy,
  Wallet,
  Clapperboard,
  Flag,
  Share2,
  Sparkles,
  Users as UsersIcon,
  Film,
} from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { useAuth } from "@/hooks/useAuth";
import { useWalletNewCount } from "@/lib/walletDiscovery";

interface ProfileMenuProps {
  // Kept for API compatibility; Rewards now lives in the header only (PR-12.1).
  onOpenRewards?: () => void;
}

export function ProfileMenu(_props: ProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const walletNew = useWalletNewCount();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // PR-NAV-FIX — close the menu on any route change so returning to a
  // previous screen never re-opens an overlay. Prevents the "back reopens menu"
  // regression.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // PR-23 — when no session, hide the profile icon entirely. Login/Signup
  // live only as primary header buttons, eliminating the duplicate entry.
  useEffect(() => {
    if (!isAuthenticated) {
      void trackEvent("auth_profile_menu_cleaned", { reason: "no_session" });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      void trackEvent("profile_menu_fixed", { opened: true });
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const close = () => setOpen(false);

  const handleLogout = async () => {
    if (signingOut) return;
    // PR-UX-VIRAL-01 — Warn the user if they have pending predictions
    // that would be lost on sign-out. localStorage flag set by the
    // PredictionHubSheet whenever a draft is created.
    try {
      const hasPending = typeof window !== "undefined" &&
        (window.localStorage.getItem("ss2026:pending_predictions") === "1" ||
         window.localStorage.getItem("vs:pending_predictions") === "1");
      if (hasPending) {
        const ok = window.confirm(
          "Tienes predicciones sin guardar. Si cierras sesión las perderás. ¿Continuar?"
        );
        if (!ok) return;
      }
    } catch { /* ignore */ }
    setSigningOut(true);
    void trackEvent("user_control_hub_logout", {});
    try {
      await logout();
    } catch { /* ignore */ }
    try {
      const keysToClear = [
        "ss2026_registered",
        "ss2026_session_id",
        "ss2026_role",
      ];
      for (const k of keysToClear) window.localStorage.removeItem(k);
    } catch { /* ignore */ }
    setOpen(false);
    // Hard redirect ensures every in-memory overlay/state is reset.
    window.location.replace("/welcome");
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        aria-label="Profile"
        aria-expanded={open}
        className="flex h-9 w-9 items-center justify-center rounded-full glass transition hover:bg-accent ring-1 ring-border/40 hover:ring-primary/40"
      >
        <User className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-11 z-50 w-64 overflow-hidden rounded-2xl border border-border/60 glass shadow-2xl backdrop-blur-xl animate-in fade-in slide-in-from-top-2 duration-150"
        >
          <div className="border-b border-border/60 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 ring-1 ring-primary/30">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-[12px] font-black text-foreground">
                  Tu cuenta
                </div>
                <div className="truncate text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                  Sesión activa
                </div>
              </div>
            </div>
          </div>

          <div className="p-1.5">
            <MenuSection label="Identidad">
              <MenuLink
                to="/mi-plan"
                icon={<User className="h-4 w-4 text-primary" />}
                label="Mi Perfil"
                onClose={close}
              />
              <MenuLink
                to="/mi-identidad"
                icon={<Shield className="h-4 w-4 text-primary" />}
                label="Mi Identidad"
                onClose={close}
              />
              <MenuLink
                to="/mi-plan"
                icon={<CreditCard className="h-4 w-4 text-primary" />}
                label="Mi Plan"
                onClose={close}
              />
            </MenuSection>

            <div className="my-1.5 h-px bg-border/50" />

            <MenuSection label="Juega y gana">
              <MenuLink
                to="/quinielas"
                icon={<Trophy className="h-4 w-4 text-[oklch(0.85_0.18_85)]" />}
                label="Quinielas"
                onClose={close}
              />
              <MenuLink
                to="/mi-identidad"
                icon={<Wallet className="h-4 w-4 text-[oklch(0.82_0.24_145)]" />}
                label="Mi Wallet"
                onClose={close}
                badge={walletNew}
              />
              <MenuLink
                to="/creator-studio"
                icon={<Clapperboard className="h-4 w-4 text-primary" />}
                label="Creator Studio"
                onClose={close}
              />
            </MenuSection>

            <div className="my-1.5 h-px bg-border/50" />

            <MenuSection label="Descubre">
              <MenuLink
                to="/nation-wars"
                icon={<Flag className="h-4 w-4 text-amber-400" />}
                label="Nation Wars"
                onClose={close}
              />
              <MenuLink
                to="/viral-cards"
                icon={<Share2 className="h-4 w-4 text-pink-400" />}
                label="Viral Cards"
                onClose={close}
              />
              <MenuLink
                to="/creator-studio"
                icon={<Sparkles className="h-4 w-4 text-primary" />}
                label="Creator Studio"
                onClose={close}
              />
              <MenuLink
                to="/media-blitz"
                icon={<Film className="h-4 w-4 text-[oklch(0.78_0.18_70)]" />}
                label="Media Blitz"
                onClose={close}
              />
              <MenuLink
                to="/social"
                icon={<UsersIcon className="h-4 w-4 text-[oklch(0.82_0.24_145)]" />}
                label="Social"
                onClose={close}
              />
            </MenuSection>

            <div className="my-1.5 h-px bg-border/50" />

            <MenuSection label="Navegación">
              <MenuButton
                icon={<Home className="h-4 w-4 text-foreground/80" />}
                label="Inicio"
                onClick={() => {
                  close();
                  void trackEvent("user_control_hub_nav", { target: "home" });
                  void navigate({ to: "/" });
                }}
              />
              <MenuButton
                icon={<Play className="h-4 w-4 text-foreground/80" />}
                label="Ver Demo"
                onClick={() => {
                  close();
                  void trackEvent("user_control_hub_nav", { target: "demo" });
                  void navigate({ to: "/", search: { demo: "1" } as never });
                }}
              />
              <MenuLink
                to="/ayuda"
                icon={<HelpCircle className="h-4 w-4 text-foreground/80" />}
                label="Ayuda"
                onClose={close}
              />
              <MenuLink
                to="/ambassadors"
                icon={<Megaphone className="h-4 w-4 text-foreground/80" />}
                label="Embajadores"
                onClose={close}
              />
              <MenuLink
                to="/ajustes/tema"
                icon={<Settings className="h-4 w-4 text-foreground/80" />}
                label="Ajustes"
                onClose={close}
              />
            </MenuSection>

            <div className="my-1.5 h-px bg-border/50" />

            <button
              role="menuitem"
              onClick={handleLogout}
              disabled={signingOut}
              className="group flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-[12px] font-bold text-destructive transition hover:bg-destructive/10 disabled:opacity-60"
            >
              <span className="flex items-center gap-2">
                <LogOut className="h-4 w-4" />
                {signingOut ? "Cerrando sesión…" : "Cerrar sesión"}
              </span>
              <ChevronRight className="h-3.5 w-3.5 opacity-50 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="px-2.5 pb-1 pt-1 text-[9px] font-black uppercase tracking-[0.15em] text-muted-foreground/70">
        {label}
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function MenuLink({
  to,
  icon,
  label,
  onClose,
  badge,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClose: () => void;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onClose}
      className="group flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-[12px] font-bold text-foreground transition hover:bg-accent"
    >
      <span className="flex items-center gap-2">
        {icon}
        {label}
      </span>
      <span className="flex items-center gap-1.5">
        {badge && badge > 0 ? (
          <span
            aria-label={`${badge} nuevos`}
            className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-black leading-none text-primary-foreground"
          >
            {badge > 9 ? "9+" : badge}
          </span>
        ) : null}
        <ChevronRight className="h-3.5 w-3.5 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-60" />
      </span>
    </Link>
  );
}

function MenuButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="group flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-[12px] font-bold text-foreground transition hover:bg-accent"
    >
      <span className="flex items-center gap-2">
        {icon}
        {label}
      </span>
      <ChevronRight className="h-3.5 w-3.5 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-60" />
    </button>
  );
}
